import { useEffect, useState } from 'react';
import { APP_VERSION, CHANGELOG, PUBLISHER, TECH_STACK } from '../version';
import { useSettings, useUpdateSetting } from '../lib/settings';
import { useAuthStore } from '../lib/authStore';
import { apiErrorMessage } from '../lib/api';
import { DiagnosticsCard } from '../components/DiagnosticsCard';
import logoUrl from '../assets/stromo_logo.svg';

export function AboutPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const settingsQ = useSettings();
  const updateMut = useUpdateSetting();

  const [licenseEdit, setLicenseEdit] = useState(false);
  const [licenseDraft, setLicenseDraft] = useState('');

  useEffect(() => {
    if (!licenseEdit) {
      setLicenseDraft(settingsQ.data?.licensed_to ?? '');
    }
  }, [settingsQ.data, licenseEdit]);

  const handleLicenseSave = () => {
    updateMut.mutate(
      { key: 'licensed_to', value: licenseDraft.trim() || null },
      { onSuccess: () => setLicenseEdit(false) },
    );
  };

  return (
    <>
      <header className="topbar">
        <div className="topbar-title">Über Stromo</div>
      </header>

      <div className="content" style={{ maxWidth: 880 }}>
        {/* Kopfbereich mit Logo, Version, Herausgeber */}
        <div className="card" style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <img src={logoUrl} alt="Stromo" style={{ height: 72 }} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-navy)' }}>
              {PUBLISHER.product}
            </div>
            <div style={{ fontSize: 15, color: 'var(--color-text-soft)', marginTop: 2 }}>
              Version{' '}
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--color-primary)' }}>
                {APP_VERSION}
              </span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 10 }}>
              Herausgeber:{' '}
              <a
                href={PUBLISHER.url}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 600 }}
              >
                {PUBLISHER.name}
              </a>
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 2 }}>
              {PUBLISHER.copyright}
            </div>
          </div>
        </div>

        {/* Lizenziert-Box */}
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Lizenziert für</div>
          {settingsQ.isLoading ? (
            <div className="loading" style={{ padding: 8 }}>Lade …</div>
          ) : licenseEdit ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                value={licenseDraft}
                onChange={(e) => setLicenseDraft(e.target.value)}
                placeholder="z.B. Elektro H. Hauri AG, Aarau"
                style={{ flex: 1, minWidth: 240 }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleLicenseSave}
                disabled={updateMut.isPending}
              >
                {updateMut.isPending ? 'Speichere …' : 'Speichern'}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setLicenseEdit(false);
                  setLicenseDraft(settingsQ.data?.licensed_to ?? '');
                }}
              >
                Abbrechen
              </button>
              {updateMut.isError && (
                <div className="login-error" style={{ width: '100%' }}>
                  {apiErrorMessage(updateMut.error)}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 600,
                  color: settingsQ.data?.licensed_to
                    ? 'var(--color-text)'
                    : 'var(--color-text-soft)',
                  fontStyle: settingsQ.data?.licensed_to ? 'normal' : 'italic',
                }}
              >
                {settingsQ.data?.licensed_to || 'noch nicht erfasst'}
              </div>
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setLicenseEdit(true)}
                >
                  Bearbeiten
                </button>
              )}
            </div>
          )}
          {!isAdmin && (
            <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 8 }}>
              Nur Admins können diesen Eintrag ändern.
            </div>
          )}
        </div>

        {/* Diagnose (nur Admin) */}
        {isAdmin && <DiagnosticsCard />}

        {/* Tech-Stack */}
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Technologien</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            {TECH_STACK.map((group) => (
              <div key={group.category}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: 'var(--color-text-soft)',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    marginBottom: 6,
                  }}
                >
                  {group.category}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13.5 }}>
                  {group.items.map((item) => (
                    <li
                      key={item}
                      style={{
                        padding: '4px 0',
                        color: 'var(--color-text-strong)',
                      }}
                    >
                      <span style={{ color: 'var(--color-primary)', marginRight: 6 }}>•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Changelog */}
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-title">Versions-Historie</div>
          {CHANGELOG.map((entry, idx) => (
            <div
              key={entry.version}
              style={{
                paddingTop: idx === 0 ? 0 : 14,
                paddingBottom: 14,
                borderTop: idx === 0 ? 'none' : '1px solid var(--color-border-soft)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 12,
                  marginBottom: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 15,
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    background: 'var(--color-planning-bg)',
                    padding: '2px 10px',
                    borderRadius: 6,
                  }}
                >
                  v{entry.version}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--color-text-soft)' }}>
                  {entry.date.split('-').reverse().join('.')}
                </span>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13.5 }}>
                {entry.notes.map((note) => (
                  <li
                    key={note}
                    style={{
                      padding: '4px 0',
                      color: 'var(--color-text-strong)',
                      paddingLeft: 14,
                      position: 'relative',
                    }}
                  >
                    <span style={{ position: 'absolute', left: 0, color: 'var(--color-text-soft)' }}>›</span>
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 24,
            fontSize: 12,
            color: 'var(--color-text-soft)',
            textAlign: 'center',
          }}
        >
          Entwickelt von{' '}
          <a
            href={PUBLISHER.url}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}
          >
            sileo.swiss
          </a>
        </div>
      </div>
    </>
  );
}
